import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { cn } from '@/lib/utils';
import type { EpicOrganization, SearchResult } from '../../../shared/types/epic-organization';

interface OrganizationSearchProps {
  onSelect: (organization: EpicOrganization) => void;
  selectedOrg?: EpicOrganization | null;
  placeholder?: string;
  className?: string;
}

export function OrganizationSearch({
  onSelect,
  selectedOrg,
  placeholder,
  className,
}: OrganizationSearchProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('login.searchOrganizationPlaceholder');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset city when state changes
  useEffect(() => {
    setSelectedCity('');
  }, [selectedState]);

  // Fetch organizations based on search query
  const { data: searchResults, isLoading: isSearching } = useQuery<SearchResult>({
    queryKey: ['organizations', 'search', debouncedQuery, selectedState, selectedCity],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedQuery.trim()) {
        params.append('q', debouncedQuery.trim());
      }
      if (selectedState) {
        params.append('state', selectedState);
      }
      if (selectedCity) {
        params.append('city', selectedCity);
      }
      params.append('limit', '20');

      const response = await fetch(`/api/organizations/search?${params}`);
      if (!response.ok) throw new Error('Search failed');
      return response.json();
    },
    enabled: debouncedQuery.length >= 2 || selectedState.length > 0 || selectedCity.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch popular organizations for initial display
  const { data: popularOrgs } = useQuery<EpicOrganization[]>({
    queryKey: ['organizations', 'popular'],
    queryFn: async () => {
      const response = await fetch('/api/organizations/popular?limit=10');
      if (!response.ok) throw new Error('Failed to fetch popular organizations');
      return response.json();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch available states for filtering
  const { data: states } = useQuery<Array<{ code: string; count: number }>>({
    queryKey: ['organizations', 'states'],
    queryFn: async () => {
      const response = await fetch('/api/organizations/states');
      if (!response.ok) throw new Error('Failed to fetch states');
      return response.json();
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Fetch cities for the selected state, filtered by search query
  const { data: cities } = useQuery<Array<{ city: string; count: number }>>({
    queryKey: ['organizations', 'cities', selectedState, debouncedQuery],
    queryFn: async () => {
      if (!selectedState) return [];
      const params = new URLSearchParams({ state: selectedState });
      if (debouncedQuery.length >= 2) {
        params.append('q', debouncedQuery);
      }
      const response = await fetch(`/api/organizations/cities?${params}`);
      if (!response.ok) throw new Error('Failed to fetch cities');
      return response.json();
    },
    enabled: !!selectedState,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const handleSelect = useCallback((org: EpicOrganization) => {
    onSelect(org);
    setOpen(false);
    setSearchQuery('');
  }, [onSelect]);

  const displayOrganizations = searchResults?.organizations || popularOrgs || [];
  const showPopularLabel = !debouncedQuery && !selectedState && popularOrgs;

  return (
    <div className={cn("w-full relative", className)}>
      {/* Backdrop overlay when popover is open */}
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-[90]"
          onClick={() => setOpen(false)}
        />
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between items-center h-auto min-h-[44px] px-4 py-3"
          >
            <ChevronDown className="mr-3 h-5 w-5 shrink-0 opacity-50" />
            {selectedOrg ? (
              <div className="flex flex-col items-start gap-0.5 text-left flex-1 min-w-0">
                <span className="font-medium truncate w-full">{selectedOrg.displayName}</span>
                {selectedOrg.address?.address1 && (
                  <span className="text-xs text-muted-foreground truncate w-full">
                    {selectedOrg.address.address1}
                  </span>
                )}
                {selectedOrg.address?.address2 && (
                  <span className="text-xs text-muted-foreground truncate w-full">
                    {selectedOrg.address.address2}
                  </span>
                )}
                {selectedOrg.address && (selectedOrg.address.city || selectedOrg.address.state || selectedOrg.address.postalCode) && (
                  <span className="text-xs text-muted-foreground truncate w-full">
                    {[
                      [selectedOrg.address.city, selectedOrg.address.state].filter(Boolean).join(', '),
                      selectedOrg.address.postalCode
                    ].filter(Boolean).join(' ')}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground flex-1 text-left">{resolvedPlaceholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 z-[100] bg-white"
          align="start"
          sideOffset={8}
          style={{ maxHeight: '400px' }}
        >
          <Command shouldFilter={false} className="max-h-[400px] bg-white">
            <div className="flex items-center border-b px-3 bg-white">
              <CommandInput
                placeholder={t('login.searchPlaceholder')}
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="flex h-11 bg-white placeholder:text-gray-600"
              />
              {isSearching && (
                <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
              )}
            </div>

            {/* State filter */}
            {states && states.length > 0 && (
              <div className="border-b px-3 py-2 bg-white">
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full h-8 px-2 text-sm border rounded-md bg-white"
                >
                  <option value="">{t('login.allStates')}</option>
                  {states.map(({ code }) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* City filter - only show when state is selected */}
            {selectedState && cities && cities.length > 0 && (
              <div className="border-b px-3 py-2 bg-white">
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full h-8 px-2 text-sm border rounded-md bg-white"
                >
                  <option value="">{t('login.allCitiesInState', { state: selectedState })}</option>
                  {cities.map(({ city }) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <CommandList className="max-h-[280px] overflow-y-auto bg-white">
              <CommandEmpty>
                {debouncedQuery.length < 2 && !selectedState
                  ? t('login.typeToSearchHint')
                  : t('login.noOrganizationsFound')}
              </CommandEmpty>

              {displayOrganizations.length > 0 && (
                <CommandGroup
                  heading={showPopularLabel ? t('login.popularOrganizations') : t('login.organizations')}
                >
                  {displayOrganizations.map((org) => (
                    <CommandItem
                      key={org.id}
                      value={org.id}
                      onSelect={() => handleSelect(org)}
                      className="flex items-start gap-3 py-3 hover:bg-accent text-foreground data-[selected=true]:bg-accent"
                    >
                      <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span className="font-medium truncate">
                          {org.displayName}
                        </span>
                        {org.address?.address1 && (
                          <span className="text-xs text-muted-foreground truncate">
                            {org.address.address1}
                          </span>
                        )}
                        {org.address?.address2 && (
                          <span className="text-xs text-muted-foreground truncate">
                            {org.address.address2}
                          </span>
                        )}
                        {org.address && (org.address.city || org.address.state || org.address.postalCode) && (
                          <span className="text-xs text-muted-foreground truncate">
                            {[
                              [org.address.city, org.address.state].filter(Boolean).join(', '),
                              org.address.postalCode
                            ].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {searchResults && searchResults.hasMore && (
                <div className="p-2 text-center text-xs text-muted-foreground border-t">
                  {t('login.showingResultsCount', { shown: searchResults.organizations.length, total: searchResults.total })}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
